// Date range selector for backtests. Mirrors TradingView's presets (Last 7/30/90/365 days,
// Entire history) plus a custom range input. Emits { from, to } as ISO-8601 UTC strings
// (or null/null for "entire history") so BacktestPanel can pass them straight into
// BacktestRequestDto.FromDate / ToDate.
//
// Presets that exceed Hyperliquid's 5000-bar history wall for the currently-selected
// interval are flagged as "out of range" so the user doesn't pick a window that's
// physically impossible to fetch (e.g. Last 30 days on 1m candles = ~43k bars).

import { useState } from "react";
import type { CandleInterval } from "../../api/types";

export interface DateRange {
  from: string | null;  // ISO-8601 UTC, inclusive
  to: string | null;    // ISO-8601 UTC, exclusive
  label: string;        // human label for display ("Last 7 days", "Entire history", "Custom")
}

export const ENTIRE_HISTORY: DateRange = { from: null, to: null, label: "Entire history" };

interface Props {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  /** Used to gray out presets that exceed Hyperliquid's 5000-bar history wall for this interval. */
  interval?: CandleInterval;
}

const PRESETS: { days: number; label: string }[] = [
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last 365 days" },
];

/** Hyperliquid's hard upstream cap — see HyperliquidCandleProvider.MaxBarsPerRequest. */
const HYPERLIQUID_MAX_BARS = 5000;

/** Minutes per bar for each interval, used to estimate bar count for a date range. */
const MINUTES_PER_BAR: Record<CandleInterval, number> = {
  "1m":  1,
  "3m":  3,
  "5m":  5,
  "15m": 15,
  "30m": 30,
  "1h":  60,
  "2h":  120,
  "4h":  240,
  "6h":  360,
  "8h":  480,
  "12h": 720,
  "1d":  1440,
  "3d":  4320,
  "1w":  10080,
  "1M":  43200,
};

/** Maximum lookback days that fit inside Hyperliquid's 5000-bar history for an interval. */
function maxLookbackDays(interval: CandleInterval): number {
  const mins = MINUTES_PER_BAR[interval];
  return Math.floor((HYPERLIQUID_MAX_BARS * mins) / 1440);
}

export function DateRangePicker({ value, onChange, disabled, interval }: Props) {
  const [open, setOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");

  const horizonDays = interval ? maxLookbackDays(interval) : Infinity;

  const applyPreset = (days: number, label: string) => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86_400_000);
    onChange({ from: from.toISOString(), to: to.toISOString(), label });
    setOpen(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    const fromIso = new Date(customFrom + "T00:00:00Z").toISOString();
    const toIso = new Date(customTo + "T23:59:59Z").toISOString();
    onChange({ from: fromIso, to: toIso, label: `${customFrom} → ${customTo}` });
    setOpen(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        style={{
          padding: "6px 12px",
          background: "#21262d",
          color: "#c9d1d9",
          border: "1px solid #30363d",
          borderRadius: 4,
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 12,
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        📅 {value.label}
        <span style={{ color: "#8b949e" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: 4,
            background: "#0d1117",
            border: "1px solid #30363d",
            borderRadius: 6,
            padding: 8,
            zIndex: 10,
            minWidth: 260,
            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          {interval && (
            <div style={{ fontSize: 10, color: "#8b949e", marginBottom: 6, lineHeight: 1.45 }}>
              <strong style={{ color: "#c9d1d9" }}>{interval}</strong> candles —
              Hyperliquid stores ≈ {horizonDays.toLocaleString()} days of history.
            </div>
          )}
          <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", marginBottom: 6 }}>
            Presets
          </div>
          {PRESETS.map((p) => {
            const outOfRange = p.days > horizonDays;
            return (
              <div
                key={p.days}
                onClick={outOfRange ? undefined : () => applyPreset(p.days, p.label)}
                title={outOfRange
                  ? `Exceeds the ${horizonDays}-day Hyperliquid history wall for ${interval}.`
                  : undefined}
                style={{
                  ...presetRowStyle,
                  color: outOfRange ? "#5a6066" : "#c9d1d9",
                  cursor: outOfRange ? "not-allowed" : "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{p.label}</span>
                {outOfRange && <span style={{ fontSize: 9, color: "#d29922" }}>⚠ out of range</span>}
              </div>
            );
          })}
          <div
            onClick={() => { onChange(ENTIRE_HISTORY); setOpen(false); }}
            style={presetRowStyle}
            title={interval ? `Uses GetLatest(${HYPERLIQUID_MAX_BARS}) — the most recent ${horizonDays} days.` : undefined}
          >
            Entire history
          </div>

          <div style={{ borderTop: "1px solid #30363d", margin: "8px 0" }} />

          <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", marginBottom: 6 }}>
            Custom range
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#8b949e" }}>
              From
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={dateInputStyle}
              />
            </label>
            <label style={{ fontSize: 11, color: "#8b949e" }}>
              To
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={dateInputStyle}
              />
            </label>
            <button
              onClick={applyCustom}
              disabled={!customFrom || !customTo}
              style={{
                padding: "4px 8px",
                background: "#238636",
                color: "#fff",
                border: "1px solid #2ea043",
                borderRadius: 3,
                cursor: !customFrom || !customTo ? "not-allowed" : "pointer",
                fontSize: 11,
                opacity: !customFrom || !customTo ? 0.5 : 1,
              }}
            >
              Apply custom range
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const presetRowStyle: React.CSSProperties = {
  padding: "6px 8px",
  fontSize: 12,
  color: "#c9d1d9",
  cursor: "pointer",
  borderRadius: 3,
};

const dateInputStyle: React.CSSProperties = {
  width: "100%",
  marginTop: 2,
  padding: "4px 6px",
  background: "#161b22",
  color: "#c9d1d9",
  border: "1px solid #30363d",
  borderRadius: 3,
  fontSize: 11,
};
